import React, { useState, useMemo } from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    Divider,
    Avatar,
    Stack,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    RadioGroup,
    Radio,
    Switch,
    Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import SkillsSection from '../components/profile/SkillsSection';
import EducationSection from '../components/profile/EducationSection';
import CertificationSection from '../components/profile/CertificationSection';
import LanguageSection from '../components/profile/LanguageSection';
import JobExperienceSection from '../components/profile/JobExperienceSection';
import ReferenceSection from '../components/profile/ReferenceSection';
import useProfile from '../hooks/UseProfile';
import useSaveProfile from '../hooks/UserSaveProfile';
import useSkills from '../hooks/useSkills';
import useEducations from '../hooks/useEducations';
import useLanguages from '../hooks/useLanguages';
import ProfileEditScreen from '../components/ProfileEditScreen';
import ProfileCompletionWidget from '../components/profile/ProfileCompletionWidget';
import { meetsProfileVisibilityMinimum } from '../utils/profileCompletion';
import { HttpService } from 'core/services/http';
import EditIcon from '@mui/icons-material/Edit';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import WorkIcon from '@mui/icons-material/Work';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import PeopleIcon from '@mui/icons-material/People';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import useProfilePhotoCropFlow from '../hooks/useProfilePhotoCropFlow';
import useProfileHeaderUpload from '../hooks/useProfileHeaderUpload';
import {
    defaultProfileHeaderGradient,
    getProfileHeaderUrl,
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';

const JobseekerProfilePage = () => {
    const { data: profile, isLoading, error, refetch } = useProfile();
    const { mutate: saveProfilePrefs, isLoading: savingPrefs } = useSaveProfile();
    const { skills } = useSkills(null);
    const { educations } = useEducations(null);
    const { languages } = useLanguages(null);
    const { enqueueSnackbar } = useSnackbar();
    const [isEditing, setIsEditing] = useState(false);
    const user = HttpService.getUser();
    const [skillsAddTrigger, setSkillsAddTrigger] = useState(0);
    const [educationAddTrigger, setEducationAddTrigger] = useState(0);
    const [certificationsAddTrigger, setCertificationsAddTrigger] = useState(0);
    const [languagesAddTrigger, setLanguagesAddTrigger] = useState(0);
    const [workExperienceAddTrigger, setWorkExperienceAddTrigger] = useState(0);
    const [referencesAddTrigger, setReferencesAddTrigger] = useState(0);
    const [completionAlertVisible, setCompletionAlertVisible] = useState(true);

    const { fileInputRef, handleFileChange, cropDialog } = useProfilePhotoCropFlow({
        profile,
        onUploaded: () => refetch(),
        enqueueSnackbar,
    });

    const {
        inputRef: headerInputRef,
        uploading: headerUploading,
        pickFile: pickHeaderFile,
        onFileChange: onHeaderFileChange,
        removeHeader,
    } = useProfileHeaderUpload({
        profile,
        onDone: () => refetch(),
        enqueueSnackbar,
    });

    const sectionLists = useMemo(
        () => ({ skills: skills || [], educations: educations || [], languages: languages || [] }),
        [skills, educations, languages]
    );

    if (isLoading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Typography>Loading profile...</Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Typography color="error">Failed to load profile</Typography>
            </Container>
        );
    }

    if (isEditing) {
        return (
            <Container maxWidth="lg">
                <ProfileEditScreen
                    profile={profile}
                    onCancel={() => setIsEditing(false)}
                />
            </Container>
        );
    }

    const allowSelectPublic =
        meetsProfileVisibilityMinimum(profile, sectionLists) || !!profile?.profile_public;

    const onVisibilityChange = (e) => {
        const nextPublic = e.target.value === 'public';
        saveProfilePrefs(
            { id: profile.id, profile_public: nextPublic },
            {
                onSuccess: () => {
                    refetch();
                    enqueueSnackbar(
                        nextPublic
                            ? 'Your profile is visible in candidate search'
                            : 'Your profile is hidden from candidate search',
                        { variant: 'success' }
                    );
                },
                onError: () => {
                    enqueueSnackbar(
                        'Could not update visibility. Add contact details, location, about, or at least one skill, education, or language.',
                        { variant: 'error' }
                    );
                },
            }
        );
    };

    const onReadyToWorkChange = (e) => {
        const checked = e.target.checked;
        saveProfilePrefs(
            { id: profile.id, ready_to_work: checked },
            {
                onSuccess: () => {
                    refetch();
                    enqueueSnackbar('Work availability updated', { variant: 'success' });
                },
                onError: () => enqueueSnackbar('Could not update work availability', { variant: 'error' }),
            }
        );
    };

    const sectionCard = (icon, title, addTriggerSetter, children) => (
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
                    <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
                </Stack>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addTriggerSetter(prev => prev + 1)}
                    sx={{ minWidth: 0 }}
                >
                    Add
                </Button>
            </Box>
            {children}
        </Paper>
    );

    return (
        <Container maxWidth="lg">
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
            />
            <input
                ref={headerInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={onHeaderFileChange}
            />
            {cropDialog}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight={700}>My Profile</Typography>
                <Button
                    variant="outlined"
                    disableElevation
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                >
                    Edit Profile
                </Button>
            </Box>

            <Grid container spacing={2}>
                {/* Profile header */}
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ position: 'relative', height: { xs: 120, sm: 160 } }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    background: getProfileHeaderUrl(profile)
                                        ? undefined
                                        : defaultProfileHeaderGradient(profile?.id ?? profile?.user),
                                    backgroundImage: getProfileHeaderUrl(profile)
                                        ? `url(${getProfileHeaderUrl(profile)})`
                                        : undefined,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                }}
                            />
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    position: 'absolute',
                                    bottom: 12,
                                    right: 12,
                                    alignItems: 'center',
                                }}
                            >
                                {headerUploading && <CircularProgress size={22} sx={{ color: 'common.white' }} />}
                                <Button
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    onClick={pickHeaderFile}
                                    disabled={headerUploading}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: 'common.white',
                                        bgcolor: 'rgba(255, 255, 255, 0.14)',
                                        backdropFilter: 'blur(14px) saturate(1.25)',
                                        WebkitBackdropFilter: 'blur(14px) saturate(1.25)',
                                        border: '1px solid rgba(255, 255, 255, 0.32)',
                                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22), 0 4px 20px rgba(0,0,0,0.18)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255, 255, 255, 0.22)',
                                            borderColor: 'rgba(255, 255, 255, 0.48)',
                                        },
                                    }}
                                >
                                    {profile?.header_image ? 'Change cover' : 'Add cover'}
                                </Button>
                                {profile?.header_image ? (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disableElevation
                                        onClick={removeHeader}
                                        disabled={headerUploading}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            color: 'rgba(255, 232, 232, 0.98)',
                                            bgcolor: 'rgba(180, 45, 45, 0.32)',
                                            backdropFilter: 'blur(14px) saturate(1.25)',
                                            WebkitBackdropFilter: 'blur(14px) saturate(1.25)',
                                            border: '1px solid rgba(255, 160, 160, 0.42)',
                                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 4px 20px rgba(0,0,0,0.18)',
                                            '&:hover': {
                                                bgcolor: 'rgba(200, 55, 55, 0.42)',
                                                borderColor: 'rgba(255, 190, 190, 0.55)',
                                            },
                                        }}
                                    >
                                        Remove
                                    </Button>
                                ) : null}
                            </Stack>
                        </Box>
                        <Box sx={{ p: 2.5, pt: 0, position: 'relative' }}>
                        <Stack direction="row" spacing={2.5} alignItems="flex-start" sx={{ mt: { xs: 1, sm: 2 } }}>
                            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                <Avatar
                                    component="button"
                                    type="button"
                                    src={getProfileAvatarUrl(profile) || undefined}
                                    onClick={() => fileInputRef.current?.click()}
                                    aria-label="Change profile photo"
                                    sx={{
                                        width: { xs: 88, sm: 96 },
                                        height: { xs: 88, sm: 96 },
                                        bgcolor: nameToAvatarColor(getProfileDisplayName(profile, user)),
                                        color: 'common.white',
                                        fontSize: 28,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        border: '3px solid',
                                        borderColor: 'background.paper',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                        p: 0,
                                        '&:hover': { opacity: 0.92, borderColor: 'text.disabled' },
                                    }}
                                >
                                    {getProfileAvatarLetter(profile, user)}
                                </Avatar>
                                <Box
                                    component="span"
                                    sx={{
                                        position: 'absolute',
                                        bottom: -2,
                                        right: -2,
                                        width: 26,
                                        height: 26,
                                        borderRadius: '50%',
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        pointerEvents: 'none',
                                        boxShadow: 1,
                                    }}
                                >
                                    <PhotoCameraIcon sx={{ fontSize: 14 }} />
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    component="button"
                                    type="button"
                                    variant="h6"
                                    fontWeight={700}
                                    gutterBottom
                                    onClick={() => setIsEditing(true)}
                                    sx={{
                                        display: 'block',
                                        border: 'none',
                                        background: 'none',
                                        padding: 0,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        color: 'text.primary',
                                        fontFamily: 'inherit',
                                        '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                                    }}
                                >
                                    {profile?.first_name} {profile?.last_name}
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 0.5 }}>
                                    {profile?.email && (
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <EmailIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                            <Typography variant="body2" color="text.secondary">{profile.email}</Typography>
                                        </Stack>
                                    )}
                                    {profile?.phone_number && (
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <PhoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                            <Typography variant="body2" color="text.secondary">{profile.phone_number}</Typography>
                                        </Stack>
                                    )}
                                    {profile?.city && (
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <LocationOnIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {profile.city}{profile?.country?.name ? `, ${profile.country.name}` : ''}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {profile?.website && (
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <LanguageIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                            <Typography
                                                variant="body2"
                                                color="primary"
                                                component="a"
                                                href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                {profile.website.replace(/^https?:\/\//, '')}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                                {profile?.about && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                                            {profile.about}
                                        </Typography>
                                    </>
                                )}
                                {completionAlertVisible && (
                                    <Box sx={{ mt: 2 }}>
                                        <ProfileCompletionWidget
                                            profile={profile}
                                            skills={sectionLists.skills}
                                            educations={sectionLists.educations}
                                            languages={sectionLists.languages}
                                            onDismiss={() => setCompletionAlertVisible(false)}
                                        />
                                    </Box>
                                )}
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    Discoverability
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Control whether employers can find you in candidate search. You can only choose
                                    &quot;Visible&quot; after adding some profile information.
                                </Typography>
                                <FormControl component="fieldset" sx={{ width: '100%' }} disabled={savingPrefs}>
                                    <FormLabel component="legend" sx={{ typography: 'body2', color: 'text.secondary' }}>
                                        Profile visibility
                                    </FormLabel>
                                    <RadioGroup
                                        row={false}
                                        value={profile?.profile_public ? 'public' : 'private'}
                                        onChange={onVisibilityChange}
                                    >
                                        <FormControlLabel value="private" control={<Radio size="small" />} label="Private — not listed for employers" />
                                        <FormControlLabel
                                            value="public"
                                            control={<Radio size="small" />}
                                            label="Visible — show in candidate discovery"
                                            disabled={!allowSelectPublic}
                                        />
                                    </RadioGroup>
                                </FormControl>
                                {!allowSelectPublic && !profile?.profile_public && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                        Add about, phone, city or country, or at least one skill, education, or language to
                                        enable visibility.
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                }}
                            >
                                <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                    <WorkOutlineIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Work availability
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={profile?.ready_to_work !== false ? 'Open to work' : 'Not seeking'}
                                        color={profile?.ready_to_work !== false ? 'success' : 'default'}
                                        variant={profile?.ready_to_work !== false ? 'filled' : 'outlined'}
                                    />
                                </Stack>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={profile?.ready_to_work !== false}
                                            onChange={onReadyToWorkChange}
                                            disabled={savingPrefs}
                                            color="primary"
                                        />
                                    }
                                    label="I am open to work opportunities"
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    {sectionCard(
                        <WorkIcon fontSize="small" />, 'Skills', setSkillsAddTrigger,
                        <SkillsSection cv={null} compact={true} triggerAdd={skillsAddTrigger} />
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    {sectionCard(
                        <WorkHistoryIcon fontSize="small" />, 'Work Experience', setWorkExperienceAddTrigger,
                        <JobExperienceSection cv={null} compact={true} triggerAdd={workExperienceAddTrigger} />
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    {sectionCard(
                        <SchoolIcon fontSize="small" />, 'Education', setEducationAddTrigger,
                        <EducationSection cv={null} compact={true} triggerAdd={educationAddTrigger} />
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    {sectionCard(
                        <CardMembershipIcon fontSize="small" />, 'Certifications', setCertificationsAddTrigger,
                        <CertificationSection cv={null} compact={true} triggerAdd={certificationsAddTrigger} />
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    {sectionCard(
                        <LanguageIcon fontSize="small" />, 'Languages', setLanguagesAddTrigger,
                        <LanguageSection cv={null} compact={true} triggerAdd={languagesAddTrigger} />
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    {sectionCard(
                        <PeopleIcon fontSize="small" />, 'References', setReferencesAddTrigger,
                        <ReferenceSection cv={null} compact={true} triggerAdd={referencesAddTrigger} />
                    )}
                </Grid>
            </Grid>
        </Container>
    );
};

export default JobseekerProfilePage;

